import React, { useEffect } from 'react';
import { InvitationBanner } from './InvitationBanner/InvitationBanner';
import {
	DEFAULT_AVAILABLE_TIME,
	InvitationFormSection,
} from './InvitationFormSection/InvitationFormSection';
import { InvitationSuccessSection } from './InvitationSuccessSection/InvitationSuccessSection';
import { InvitationTermsSection } from './InvitationTermsSection/InvitationTermsSection';
import { RBHBox } from '../../../../shared/components';
import styles from './styles';
import './styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { mainCreators } from '../../../main-action';
import { MAIN_STATE_KEY } from '../../../main-reducer';
import {
	GetTncDetailHandlerResp,
	GetInvitationConfigHandlerResp,
	invitationSagas,
} from './redux/invitation-saga';
import { httpCreators, useLoading } from '@ppvrbh/merchant-frontend-core';
import { INVITATION_REQUEST_ID_LIST } from './redux/invitation-constant';
import { InvitationService } from './redux/invitation-service';
import { invitationCreators } from './redux/invitation-action';
import {
	INVITATION_STATE_KEY,
	InvitationConfig,
	InvitationFormData,
	invitationStateSelector,
} from './redux/invitation-reducer';
import { InvitationCampaign } from './types/campaign';

export { DEFAULT_AVAILABLE_TIME } from './InvitationFormSection/InvitationFormSection';

const defaultInvitationFormData: InvitationFormData = {
	contactName: '',
	contactNumber: '',
	availableTime: DEFAULT_AVAILABLE_TIME,
	token: '',
	campaignName: '',
	channel: null,
};

interface InvitationRootProps {
	children: React.ReactNode;
	campaign: InvitationCampaign;
	token: string | null;
	channel: string | null;
}
const InvitationRoot = ({
	children,
	token,
	channel,
	campaign,
}: InvitationRootProps) => {
	const invitationConfig = useSelector(
		invitationStateSelector(INVITATION_STATE_KEY.CONFIG),
	) as InvitationConfig;
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(mainCreators.setMainState(MAIN_STATE_KEY.HEADER_MENU, 0));
		window.scrollTo({ top: 0, behavior: 'smooth' });

		dispatch(
			httpCreators.request({
				id: INVITATION_REQUEST_ID_LIST.GET_TNC_CONFIG,
				sagaHandler: invitationSagas.getInvitationConfigHandler,
				callbackParams: {
					onSuccess: (data?: GetInvitationConfigHandlerResp) => {
						if (data) {
							dispatch(
								invitationCreators.setInvitationState(
									INVITATION_STATE_KEY.CONFIG,
									data,
								),
							);
						}
					},
				},
				service: InvitationService.getTncStatus,
				serviceParams: {
					token,
					channel,
					campaignName: campaign,
				},
			}),
		);

		dispatch(
			invitationCreators.setInvitationState(INVITATION_STATE_KEY.FORM, {
				...defaultInvitationFormData,
				token: token ?? '',
				campaignName: campaign,
				channel,
			}),
		);
	}, [dispatch, token]);

	useEffect(() => {
		if (!invitationConfig?.tncUrl) {
			return;
		}

		dispatch(
			httpCreators.request({
				id: INVITATION_REQUEST_ID_LIST.GET_TNC_DETAIL,
				sagaHandler: invitationSagas.getTncDetailHandler,
				callbackParams: {
					onSuccess: (data: GetTncDetailHandlerResp) => {
						dispatch(
							invitationCreators.setInvitationState(
								INVITATION_STATE_KEY.TNC_DETAIL,
								data,
							),
						);
					},
				},
				service: InvitationService.getTncDetail,
				serviceParams: {
					url: invitationConfig.tncUrl,
				},
			}),
		);
	}, [invitationConfig]);

	return (
		<RBHBox className='invitation-page' style={styles.page}>
			<RBHBox
				className='invitation-page-glow-left'
				style={styles.pageGlowLeft}
			/>
			<RBHBox className='invitation-page-glow' style={styles.pageGlowRight1} />
			<RBHBox className='invitation-page-glow' style={styles.pageGlowRight2} />

			<RBHBox className='invitation-content' style={styles.content}>
				<InvitationContent invitationConfig={invitationConfig}>
					{children}
				</InvitationContent>
			</RBHBox>
		</RBHBox>
	);
};

const InvitationContent = ({
	children,
	invitationConfig,
}: {
	children: React.ReactNode;
	invitationConfig: InvitationConfig;
}) => {
	const isLoading = useLoading();
	if (isLoading) {
		return null;
	}

	return (
		<RBHBox className='invitation-card' style={styles.card}>
			{invitationConfig?.isTncAccepted ? (
				<InvitationSuccessSection />
			) : (
				children
			)}
		</RBHBox>
	);
};

InvitationRoot.Banner = InvitationBanner;
InvitationRoot.FormSection = InvitationFormSection;
InvitationRoot.SuccessSection = InvitationSuccessSection;
InvitationRoot.TermsSection = InvitationTermsSection;

export { InvitationRoot as Invitation };
